import { lazy, Suspense, useCallback } from "react";
import { toast } from "sonner";
import { CollectionGrid } from "@/components/collection/CollectionGrid";
import { useCollectionData } from "@/components/collection/useCollectionData";
import { BackLink } from "@/components/renewal/BackLink";
import { RarityLadder } from "@/components/renewal/cards/RarityLadder";
import { PageHeader } from "@/components/renewal/system";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { GlowButton } from "@/components/ui/GlowButton";
import { signInWithTelegram } from "@/lib/auth";
import { CARD_UNITS } from "@/lib/renewal/mockdb";

// Complete Shelf 호스트만 지연 로드 — 원본 HTML은 iframe 문서라 JS 그래프에 미포함
const CompleteShelfLandingPage = lazy(
  () => import("@/components/effects/complete-shelf-landing-page/CompleteShelfLandingPage"),
);

/** /collection — dex-style gallery: every assistant × every finish (§2C). */
export function CollectionPage() {
  const { authenticated, loading, error, state, reload } = useCollectionData();

  const handleSignIn = useCallback(() => {
    void signInWithTelegram("/profile/collection").catch((err: unknown) => {
      toast.error(err instanceof Error ? err.message : "Telegram sign-in failed");
    });
  }, []);

  const pct =
    state && state.totalCount > 0 ? Math.round((state.ownedCount / state.totalCount) * 100) : null;

  return (
    <div className="space-y-6 sm:space-y-8">
      <BackLink />

      {/* §1.1 — eyebrow + noun-phrase title + key figures (always visible, all viewports) */}
      <PageHeader
        eyebrow="COLLECTION — SEASON 1"
        title="Collection"
        className="border-b border-line pb-6"
        stats={
          !loading && authenticated && state
            ? [
                { label: "Collected", value: `${state.ownedCount} / ${state.totalCount}` },
                { label: "Completion", value: pct != null ? `${pct}%` : null },
              ]
            : undefined
        }
      />

      {/* 캐릭터 볼륨 라이브러리 — Working Volumes 셸프(원본 문서 iframe, 스킬 계약) */}
      <section className="space-y-4" aria-label="Character volumes">
        <SectionHeader label="Library" title="Character volumes" />
        <div className="h-[560px] overflow-hidden rounded-2xl border border-line bg-(--ty-bg-2) sm:h-[660px] lg:h-[740px]">
          <Suspense fallback={<div className="h-full w-full animate-pulse bg-white/4" />}>
            <CompleteShelfLandingPage />
          </Suspense>
        </div>
      </section>

      {/* 등급 사다리 — 8피니시 낮→높 */}
      <RarityLadder />

      {/* R-10 burn entry — conversion rule as a data line, no prose (§4.1) */}
      {!loading && authenticated ? (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-line bg-white/4 px-4 py-3">
          <p className="font-mono text-[11px] font-medium tabular-nums text-ink-dim">
            Base = {CARD_UNITS.base} Unit · Holo = {CARD_UNITS.holo} Units · {CARD_UNITS.packCost}{" "}
            Units = 1 Exchange Pack
          </p>
          <GlowButton variant="ghost" to="/profile/collection/burn" className="shrink-0">
            Burn &amp; Exchange →
          </GlowButton>
        </div>
      ) : null}

      <CollectionGrid
        authenticated={authenticated}
        loading={loading}
        error={error}
        state={state}
        onSignIn={handleSignIn}
        onRetry={reload}
      />
    </div>
  );
}
